/* eslint-disable @typescript-eslint/no-explicit-any */
import { CORE_AXIOS, CORE_PLUGIN, CORE_USER, Http, type UserService } from "@/core"
import { useMiscStore, type ConsolePageOption, type ConsolePageService, type Plugin } from "@/plugins"
import { injectOptional } from "@/tsyringe-extend"
import { convertArrayToTree } from "@/utils"
import { defineStore, storeToRefs } from "pinia"
import "splitpanes/dist/splitpanes.css"
import { container, injectable, instanceCachingFactory, registry, singleton } from "tsyringe"
import { watch, type App } from "vue"
import type { RouteRecordRaw } from "vue-router"
import { PLUGIN_CONSOLE_PAGE, PLUGIN_RBAC } from "../../builtin/BuiltinPluginSymbol"
import type { MenuItem, MenuItemMap, OperationItem, RbacPageOption, RbacPageService, RbacState } from "./DeclareType"
import MenuTitle from "./view/menu/MenuTitle.vue"
import MenuTree from "./view/menu/MenuTree.vue"
import Organization from "./view/organization/organizationView.vue"
import Overview from "./view/OverviewPage.vue"
import Position from "./view/position/positionsView.vue"
import Role from "./view/role/RoleView.vue"
import SecondMenuTitle from "./view/secondMenu/MenuTitle.vue"
import SecondMenuTree from "./view/secondMenu/MenuTree.vue"
import User from "./view/user/UserView.vue"
import TabsView from "./view/TabsView.vue"
import { message } from "ant-design-vue"

export const RbacPageOptionsInjectKey = "RbacPageOptionsInjectKey";

const createState = (): RbacState => {
  return {
    disabled: false,
    menus: {
      tree: [{ id: "", children: [] }],
      idMap: {},
      pathMap: {},
      menus: [],
      menuType: sessionStorage.getItem('menuType') || '/console',//  /console  or   /mes
    },
    tabsList: [],
    activeTab: "",
    operations: [],
    models: {
      role: [
        {
          name: ""
        }
      ],
      user: [],
      organization: [],
      position: [""]
    },
    permissions: {
      hasRight: { users: null, roles: null, organizations: null, positions: null },
      assigned: { users: null, roles: null, organizations: null, positions: null },
      assignedAndChildren: { users: null, roles: null, organizations: null, positions: null }
    }
  };
};

export const useRbacStore = defineStore("rbac", {
  state: createState,
  actions: {
    async fetchMenus(): Promise<void> {
      const api = "/v1/api/authorizations/menus";
      const axios = container.resolve<Http>(CORE_AXIOS);
      const res = await axios.get(api);
      const { menus } = res as { menus: Array<MenuItem> };
      const pathMap: MenuItemMap = {};
      menus.forEach((m: MenuItem) => {
        if (m.linkTo) {
          pathMap[m.linkTo] = m;
        }
      })
      const { tree, idMap, items } = convertArrayToTree<MenuItem>(menus)
      this.menus.tree = tree
      this.menus.idMap = idMap
      this.menus.menus = items
      this.menus.pathMap = pathMap
    },
    async fetchOperations(): Promise<void> {
      const api = "/v1/api/authorizations/operations";
      const axios = container.resolve<Http>(CORE_AXIOS);
      const res = await axios.get(api);
      const { operations } = res as { operations: Array<OperationItem> };
      this.operations = operations;
    },
    async fetchUsers(): Promise<any[] | null> {
      const axios = container.resolve<Http>(CORE_AXIOS);
      return (
        this.$state.permissions.hasRight.users ||
        axios
          .get("/v1/api/authorizations/users")
          .then((res: any) => {
            this.$state.permissions.hasRight.users = res.users;
            return this.$state.permissions.hasRight.users;
          })
          .catch(() => {
            return [];
          })
      );
    },
    async fetchRoles(): Promise<any[] | null> {
      const axios = container.resolve<Http>(CORE_AXIOS);
      return (
        this.$state.permissions.hasRight.roles ||
        axios
          .get("/v1/api/authorizations/roles")
          .then((res: any) => {
            this.$state.permissions.hasRight.roles = res.roles;
            return this.$state.permissions.hasRight.roles;
          })
          .catch(() => {
            return [];
          })
      );
    },
    async fetchOrganizations(): Promise<any[] | null> {
      const axios = container.resolve<Http>(CORE_AXIOS);
      return (
        this.$state.permissions.hasRight.organizations ||
        axios
          .get("/v1/api/authorizations/organizations")
          .then((res: any) => {
            this.$state.permissions.hasRight.organizations = res.organizations;
            return this.$state.permissions.hasRight.organizations;
          })
          .catch(() => {
            return [];
          })
      );
    },
    async fetchPositions(): Promise<any[] | null> {
      const axios = container.resolve<Http>(CORE_AXIOS);
      return (
        this.$state.permissions.hasRight.positions ||
        axios
          .get("/v1/api/authorizations/positions")
          .then((res: any) => {
            this.$state.permissions.hasRight.positions = res.positions;
            return this.$state.permissions.hasRight.positions;
          })
          .catch(() => {
            return [];
          })
      );
    },
    // assgined
    async fetchAssignedRoles(): Promise<any[] | null> {
      const axios = container.resolve<Http>(CORE_AXIOS);
      const key = "assigned";
      return (
        this.$state.permissions[key].roles ||
        axios
          .get(`/v1/api/authorizations/assigned_roles`)
          .then((res: any) => {
            this.$state.permissions[key].roles = res.roles;
            return this.$state.permissions[key].roles;
          })
          .catch(() => {
            return [];
          })
      );
    },
    async fetchAssignedOrganizations(includeChildren: boolean): Promise<any[] | null> {
      const axios = container.resolve<Http>(CORE_AXIOS);
      const key = includeChildren ? "assignedAndChildren" : "assigned";
      return (
        this.$state.permissions[key].organizations ||
        axios
          .get(`/v1/api/authorizations/assigned_organizations${includeChildren ? "?include_children=true" : ""}`)
          .then((res: any) => {
            this.$state.permissions[key].organizations = res.organizations;
            return this.$state.permissions[key].organizations;
          })
          .catch(() => {
            return [];
          })
      );
    },
    async fetchAssignedPositions(includeChildren: boolean): Promise<any[] | null> {
      const axios = container.resolve<Http>(CORE_AXIOS);
      const key = includeChildren ? "assignedAndChildren" : "assigned";
      return (
        this.$state.permissions[key].positions ||
        axios
          .get(`/v1/api/authorizations/assigned_positions${includeChildren ? "?include_children=true" : ""}`)
          .then((res: any) => {
            this.$state.permissions[key].positions = res.positions;
            return this.$state.permissions[key].positions;
          })
          .catch(() => {
            return [];
          })
      )
    },
    setMenuType(value: string): void {
      this.menus.menuType = value
      sessionStorage.setItem('menuType', value)
    }
  }
});

@singleton()
@registry([
  {
    token: CORE_PLUGIN,
    useFactory: instanceCachingFactory<Plugin<RbacPageOption, RbacPageService>>((c) => c.resolve(DefaultRBACPlugin))
  }
])
@injectable()
export class DefaultRBACPlugin implements Plugin<RbacPageOption, RbacPageService> {
  id: string | symbol = PLUGIN_RBAC;
  options: RbacPageOption;
  service: RbacPageService;
  constructor(
    @injectOptional(RbacPageOptionsInjectKey)
    options: RbacPageOption
  ) {
    const instantiationStore = useRbacStore();
    this.service = instantiationStore;
    this.options = instantiationStore.$state;
    if (options) {
      instantiationStore.$patch(options);
    }
  }

  init(app: App<Element>): void {
    if (container.isRegistered(PLUGIN_RBAC)) {
      return
    }
    const rbac = useRbacStore();
    container.register<RbacPageService>(PLUGIN_RBAC, { useValue: this.service });
    const { disabled, disableRBAC } = this.options;
    if (!disabled) {
      const plugin = container.resolve<Plugin<ConsolePageOption, ConsolePageService>>(PLUGIN_CONSOLE_PAGE);
      const { template } = useMiscStore();
      if (template === 1) {
        app.component("RbacMenu", MenuTree);
        app.component("RbacTitle", MenuTitle);
      } else {
        app.component("RbacMenu", SecondMenuTree);
        app.component("RbacTitle", SecondMenuTitle);
      }
      app.component("RbacTabs", TabsView)
      if (!disableRBAC) {
        const rbacRoutes = [
          { name: "rbac-menus", path: "authorizations/overview", component: Overview },
          { name: "rbac-role", path: "authorizations/roles", component: Role },
          { name: "rbac-user", path: "authorizations/users", component: User },
          { name: "rbac-organization", path: "authorizations/organizations", component: Organization },
          { name: "rbac-position", path: "authorizations/positions", component: Position }
        ] as RouteRecordRaw[];
        rbacRoutes.forEach((r) => {
          plugin.service.addChildRoute(r);
        });
        // watch and refresh operations
        const userService = container.resolve<UserService>(CORE_USER);
        if (userService.isUserValid()) {
          rbac.fetchOperations().catch(e => {
            if (e.name === 'unauthorized error') {
              userService.reset()
              // 未登录，提示并跳转登录
              message.warn({
                content: '用户信息过期，请先登录',
                key: 'unauthorized',
                style: {
                  marginTop: '10vh',
                },
              })
              return
            }
            console.error('fetch operations failed', e)
          })
        }
        const user = userService.getUser()
        watch(
          user,
          () => {
            rbac.fetchOperations();
          },
          { deep: true }
        );
      }
      plugin.service.addLeftMiddle("rbac-menu");
      plugin.service.addHeadLeft("rbac-title")
      plugin.service.addNavigation("rbac-tabs")
    }
    // directive
    app.directive("p", {
      // 在绑定元素的父组件
      // 及他自己的所有子节点都挂载完成后调用
      // mounted(el, binding, vnode, prevVnode) {
      mounted(el, binding) {
        if (disableRBAC) {
          return;
        }
        const { operations: haveOps } = storeToRefs(rbac);
        const fn = () => {
          let neededOps = binding.value;
          if (!Array.isArray(neededOps)) {
            neededOps = [neededOps];
          }
          // find op not in operations
          const noRight = neededOps.find((op: any) => {
            return !haveOps.value.find((oh: any) => {
              return `${oh.key}:${oh.method || "GET"}` === op;
            });
          });
          if (noRight) {
            if ("disabled" in el) {
              el.disabled = true;
            } else {
              el.style.visibility = "hidden";
            }
          } else {
            if ("disabled" in el) {
              el.disabled = false;
            } else {
              el.style.visibility = "visible";
            }
          }
        };
        fn();
        el.unwatch = watch(haveOps, fn, { deep: true });
      },
      beforeUnmount(el) {
        if (el.unwatch) {
          el.unwatch();
        }
      }
    });
  }

  initialized(): void {
    //
  }
}
